import SEO from '@/components/SEO';
import { Link } from 'react-router-dom';
import { Check, X, Shield, ArrowRight, Phone, Mail } from 'lucide-react';

const covers = [
  'Component failure related to the repair performed',
  'Solder joint failure',
  'Replacement part defects',
  'Calibration drift',
  'Labour workmanship',
];

const excludes = [
  'Physical damage (drops, impacts, liquid ingress)',
  'Lamp or laser module failure (these are consumables)',
  'Unauthorised modifications after repair',
  'Damage from improper installation or operation',
  'Normal wear and tear on non-repaired components',
];

const returnsPolicy = [
  'If we cannot repair your projector, we return it at no charge (UK mainland)',
  '14-day cooling-off period on non-repair bookings (deposit refunded)',
  'Completed repairs are non-refundable — warranty cover means free recollection and re-repair of your device, not a refund',
  'If you choose not to proceed after diagnosis, £45 diagnostic fee applies',
  'All returns are tracked and insured',
];

const claimSteps = [
  { num: '1', text: 'Call us on 0333 006 4327 or email wellselectronicsltd@gmail.com' },
  { num: '2', text: 'Quote your repair reference number' },
  { num: '3', text: 'Describe the fault symptoms' },
  { num: '4', text: 'We will arrange collection within 72 hours' },
  { num: '5', text: 'Repair completed within 4-7 working days' },
];

export default function WarrantyPage() {
  return (
    <>
      <SEO title="Warranty & Returns" description="90-day warranty on all projector repairs covering parts and labour. Extended warranty options available. Clear returns policy. Make a warranty claim online." canonical="https://projectorrepairlondon.co.uk/warranty" />

      {/* Hero — Blue */}
      <section className="bg-[#0057B7] pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="container-custom">
          <nav className="mb-6"><ol className="flex items-center gap-2 text-sm">
            <li><Link to="/" className="text-white/70 hover:text-white transition-colors">Home</Link></li>
            <li className="text-white/40">/</li>
            <li className="text-white font-medium">Warranty & Returns</li>
          </ol></nav>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-white max-w-2xl leading-tight">Warranty & Returns</h1>
          <p className="mt-4 text-white/80">Clear, fair warranty terms that give you confidence in every repair.</p>
        </div>
      </section>

      {/* 90-Day Warranty — White */}
      <section className="bg-white py-12 lg:py-16">
        <div className="container-custom max-w-4xl">
          <div className="bg-[#E8F0FE] rounded-xl p-6 md:p-8 border border-[#0057B7]/10 text-center">
            <Shield className="w-12 h-12 text-[#0057B7] mx-auto mb-4" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#0057B7]">90-Day Standard Warranty</h2>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-left max-w-xl mx-auto">
              {[
                'Covers all parts and labour used in the repair',
                'If the same fault reoccurs, we repair again free of charge',
                'Starts from the date of return delivery',
                'Transferable to new owner if you sell the equipment',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#0057B7] shrink-0 mt-0.5" />
                  <span className="text-sm text-[#0057B7]/80">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What is Covered / Not Covered — Light Blue */}
      <section className="bg-[#E8F0FE] py-12 lg:py-16">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white rounded-xl p-6 border border-[#0057B7]/10">
              <h3 className="font-heading text-lg font-semibold text-[#0057B7] mb-4 flex items-center gap-2">
                <Check className="w-5 h-5 text-[#0057B7]" /> What the Warranty Covers
              </h3>
              <ul className="space-y-3">
                {covers.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-[#0057B7]/80">
                    <Check className="w-4 h-4 text-[#0057B7] shrink-0 mt-0.5" />{item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border border-[#0057B7]/10">
              <h3 className="font-heading text-lg font-semibold text-[#0057B7] mb-4 flex items-center gap-2">
                <X className="w-5 h-5 text-[#0057B7]" /> What is Not Covered
              </h3>
              <ul className="space-y-3">
                {excludes.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-[#0057B7]/80">
                    <X className="w-4 h-4 text-[#0057B7] shrink-0 mt-0.5" />{item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Extended Warranty — Yellow */}
      <section className="bg-[#FFDD00] py-12">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 items-center">
            <div>
              <span className="px-2 py-0.5 bg-[#0057B7] text-[#FFDD00] text-[10px] font-bold rounded uppercase tracking-wider">Extended Warranty</span>
              <h3 className="font-heading text-xl md:text-2xl font-bold text-[#0057B7] mt-2">Complete protection from just 45p per day</h3>
              <p className="text-sm text-[#0057B7]/80 mt-2 max-w-2xl">Covers all components — not just those we repaired. Includes priority turnaround, free collection & return, annual health check, and 24/7 technical support.</p>
              <Link to="/extended-warranty" className="mt-5 inline-flex items-center gap-2 px-6 py-3 bg-[#0057B7] text-[#FFDD00] rounded-lg font-bold hover:bg-[#003D80] transition-colors">
                View full details <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              {[{ v: '45p', l: 'per day' }, { v: '£13.69', l: 'per month' }, { v: '£164.25', l: 'per year' }].map((p) => (
                <div key={p.l} className="bg-white rounded-xl px-4 py-5 border border-[#0057B7]/10 min-w-[110px]">
                  <p className="font-heading text-2xl font-extrabold text-[#0057B7]">{p.v}</p>
                  <p className="text-xs text-[#0057B7]/70 mt-1">{p.l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Returns Policy — White */}
      <section className="bg-white py-12 lg:py-16">
        <div className="container-custom max-w-3xl">
          <h2 className="font-heading text-2xl font-bold text-[#0057B7] mb-6">Returns Policy</h2>
          <div className="bg-[#E8F0FE] rounded-xl p-6 border border-[#0057B7]/10 space-y-3">
            {returnsPolicy.map((item) => (
              <div key={item} className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#0057B7] shrink-0 mt-0.5" />
                <p className="text-[#0057B7]/80 text-sm">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Claim — Light Blue */}
      <section className="bg-[#E8F0FE] py-12 lg:py-16">
        <div className="container-custom max-w-3xl">
          <h2 className="font-heading text-2xl font-bold text-[#0057B7] mb-8 text-center">How to Make a Warranty Claim</h2>
          <div className="space-y-4">
            {claimSteps.map((step) => (
              <div key={step.num} className="flex items-start gap-4 bg-white rounded-xl p-5 border border-[#0057B7]/10">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0057B7] text-[#FFDD00] font-bold text-sm shrink-0">{step.num}</div>
                <p className="text-[#0057B7]/80 text-sm mt-1">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA — Blue */}
      <section className="bg-[#0057B7] py-16">
        <div className="container-custom text-center">
          <h2 className="font-heading text-3xl font-bold text-white">Questions about warranty?</h2>
          <p className="mt-4 text-white/80 max-w-lg mx-auto">Contact us — we are happy to clarify any aspect of our warranty coverage.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <a href="tel:03330064327" className="inline-flex items-center gap-2 px-8 py-4 bg-[#FFDD00] text-[#0057B7] rounded-lg font-bold hover:bg-[#C9A800] transition-all text-lg">
              <Phone className="w-5 h-5" /> 0333 006 4327
            </a>
            <a href="mailto:wellselectronicsltd@gmail.com" className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 text-white rounded-lg font-medium hover:bg-white/20 transition-all text-lg border border-white/20">
              <Mail className="w-5 h-5" /> Email Us
            </a>
            <Link to="/repair-enquiry" className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 text-white rounded-lg font-medium hover:bg-white/20 transition-all text-lg border border-white/20">
              Repair Enquiry <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
